// src/features/records/components/MapView/components/MapCanvas.js
import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import styles from './MapCanvas.module.css';
import RecordHoverCard from './RecordHoverCard';
import { useTheme } from '../../../../../hooks';
import { logWarning, logInfo, ErrorCategory } from '../../../../../services/errorService';

// Get Mapbox token from environment
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Validate Mapbox token
if (!mapboxgl.accessToken) {
  throw new Error(
    'Mapbox access token is required. Please set REACT_APP_MAPBOX_TOKEN in your environment variables.'
  );
}

// Animation duration constants (in milliseconds)
const ANIMATION_DURATIONS = {
  clusterFlyToBase: 600,      // Base duration for cluster zoom animation
  perZoomStep: 100,          // Additional duration per zoom level difference
  maxDuration: 1200,         // Maximum animation duration
  userLocationFlyTo: 1200,   // Duration for flying to user location
  debounceDelay: 300         // Debounce delay for viewport changes
};

// Helper function to get appropriate Mapbox style based on theme
const getMapboxStyle = (theme) => {
  return theme === 'dark' 
    ? 'mapbox://styles/mapbox/dark-v10'
    : 'mapbox://styles/mapbox/light-v11';
};

// Helper function to get CSS variable value with fallback
const getCSSVariable = (variableName, fallback = '') => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  return value || fallback;
};

// Helper function to safely set paint property with error handling
const safeSetPaintProperty = (mapInstance, layerId, property, value) => {
  try {
    if (mapInstance.getLayer(layerId)) {
      mapInstance.setPaintProperty(layerId, property, value);
    }
  } catch (error) {
    // Log as info since layer absence in certain styles is expected behavior
    logInfo(`Layer style update skipped: ${layerId}`, {
      category: ErrorCategory.RENDERING,
      component: 'MapCanvas',
      action: 'safeSetPaintProperty',
      metadata: {
        layerId,
        property,
        errorMessage: error.message
      }
    });
  }
};

// Layer creation helpers
const createClusterLayers = (mapInstance) => {
  // Cluster circles with balanced gray design
  mapInstance.addLayer({
    id: 'clusters',
    type: 'circle',
    source: 'records',
    filter: ['has', 'point_count'],
    paint: {
      // Softer gray for better visual comfort with good contrast
      'circle-color': getCSSVariable('--text-secondary', '#6B6B6B'),
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['get', 'point_count'],
        0, 20,     // 40px diameter
        10, 24,    // 48px diameter
        50, 28,    // 56px diameter
        100, 32    // 64px diameter
      ],
      'circle-stroke-width': 1,
      'circle-stroke-color': getCSSVariable('--border-medium', '#E5E5E5'),
      'circle-opacity': 1,
      'circle-radius-transition': {
        duration: 200,
        delay: 0
      }
    }
  });

  // Cluster count text with improved typography
  mapInstance.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: 'records',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': [
        'interpolate',
        ['linear'],
        ['get', 'point_count'],
        0, 16,
        10, 18,
        50, 20,
        100, 22
      ],
      'text-allow-overlap': true,
      'text-ignore-placement': true
    },
    paint: {
      // White text for good contrast on gray background
      'text-color': '#FFFFFF'
    }
  });
};

const createPinLayers = (mapInstance) => {
  // Hit-test layer for mobile touch targets (invisible)
  mapInstance.addLayer({
    id: 'pins-hitarea',
    type: 'circle',
    source: 'records',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-radius': 22, // 44px diameter = WCAG AAA touch target minimum
      'circle-opacity': 0, // Invisible
      'circle-color': 'transparent'
    }
  });

  // Visual pins - small dots with balanced gray design
  mapInstance.addLayer({
    id: 'pins',
    type: 'circle',
    source: 'records',
    filter: ['!', ['has', 'point_count']],
    paint: {
      // Use primary color for better visibility
      'circle-color': getCSSVariable('--text-primary', '#1A1A1A'),
      'circle-radius': 8, // 16px diameter visual
      'circle-stroke-width': 0, // No stroke for cleaner look
      'circle-opacity': 1,
      'circle-radius-transition': {
        duration: 200,
        delay: 0
      }
    }
  });

  // Selected pin highlight - slightly larger
  mapInstance.addLayer({
    id: 'selected-pin',
    type: 'circle',
    source: 'records',
    filter: ['==', ['get', 'id'], -1],
    paint: {
      'circle-color': getCSSVariable('--text-primary', '#1A1A1A'),
      'circle-radius': 10, // 20px diameter when selected
      'circle-stroke-width': 2,
      'circle-stroke-color': getCSSVariable('--background-elevated', '#FFFFFF'),
      'circle-stroke-opacity': 1,
      'circle-opacity': 1
    }
  });

  // Pin labels with mobile-friendly pill design
  mapInstance.addLayer({
    id: 'pin-labels',
    type: 'symbol',
    source: 'records',
    filter: ['!', ['has', 'point_count']],
    layout: {
      'text-field': ['get', 'displayTitle'],
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
      'text-size': 14, // Consistent 14px for mobile readability
      'text-offset': [1.2, 0],
      'text-anchor': 'left',
      'text-max-width': 8,
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-optional': true, // Allow hiding when crowded
      // Add padding for better spacing between labels
      'text-padding': 8,
      // Only show labels at reasonable zoom levels
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        11, 0,
        12, 1
      ]
    },
    paint: {
      'text-color': getCSSVariable('--text-primary', '#1A1A1A'),
      // Create pill effect with strong halo for touch-friendly background
      'text-halo-color': getCSSVariable('--background-elevated', '#FFFFFF'),
      'text-halo-width': 10,
      'text-halo-blur': 0
    }
  });
};

// Helper function to update layer colors based on current theme
const updateLayerColors = (mapInstance) => {
  if (!mapInstance || !mapInstance.isStyleLoaded()) return;
  
  // Update cluster colors - balanced gray design
  safeSetPaintProperty(mapInstance, 'clusters', 'circle-color', getCSSVariable('--text-secondary', '#6B6B6B'));
  safeSetPaintProperty(mapInstance, 'clusters', 'circle-stroke-color', getCSSVariable('--border-medium', '#E5E5E5'));
  
  // Cluster text stays white for contrast
  safeSetPaintProperty(mapInstance, 'cluster-count', 'text-color', '#FFFFFF');
  
  // Update pin colors - use primary for visibility
  safeSetPaintProperty(mapInstance, 'pins', 'circle-color', getCSSVariable('--text-primary', '#1A1A1A'));
  
  // Update selected pin colors
  safeSetPaintProperty(mapInstance, 'selected-pin', 'circle-color', getCSSVariable('--text-primary', '#1A1A1A'));
  safeSetPaintProperty(mapInstance, 'selected-pin', 'circle-stroke-color', getCSSVariable('--background-elevated', '#FFFFFF'));
  
  // Update pin label colors
  safeSetPaintProperty(mapInstance, 'pin-labels', 'text-color', getCSSVariable('--text-primary', '#1A1A1A'));
  safeSetPaintProperty(mapInstance, 'pin-labels', 'text-halo-color', getCSSVariable('--background-elevated', '#FFFFFF'));
};

const MapCanvas = forwardRef(({
  geoJsonData,
  records,
  selectedPinId,
  userLocation,
  onViewportChange,
  onPinClick,
  onClusterClick,
  onMapLoad,
  onError
}, ref) => {
  const { theme } = useTheme();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const lastViewportUpdate = useRef(0);
  const popup = useRef(null); // Single reusable popup for hover
  const popupContainer = useRef(null); // DOM container for React content
  const popupRoot = useRef(null); // React root for popup content
  const currentMapStyle = useRef(null); // Track current style type
  const recordsRef = useRef(records); // Keep latest records for hover

  // Store initial center to avoid stale closure in map initialization
  const initialCenterRef = useRef(
    userLocation
      ? [userLocation.longitude, userLocation.latitude]
      : [139.6503, 35.6762] // Tokyo fallback
  );
  const initialZoomRef = useRef(userLocation ? 13 : 10);

  // Keep the latest onError in a ref so the one-time map creation effect
  // can always call the up-to-date handler
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Keep records up to date
  useEffect(() => { recordsRef.current = records; }, [records]);

  // Expose map instance to parent
  useImperativeHandle(ref, () => ({
    getMap: () => map.current
  }));

  // Initialize popup instance and React root
  const initializePopup = (mapInstance) => {
    if (!popup.current) {
      popup.current = new mapboxgl.Popup({
        closeButton: false,
        offset: 20,
        className: 'record-hover-popup',
        maxWidth: 'none'
      });
    }
    
    // Create persistent container and root for React content
    if (!popupContainer.current) {
      popupContainer.current = document.createElement('div');
      popupRoot.current = ReactDOM.createRoot(popupContainer.current);
    }
  };

  // Cleanup popup React root
  const cleanupPopupRoot = () => {
    if (popupRoot.current) {
      // Just unmount the content, don't destroy the root
      popupRoot.current.render(null);
    }
  };

  // Initialize map instance only once
  useEffect(() => {
    if (map.current) return; // Initialize only once

    try {
      const initialStyle = getMapboxStyle(theme);
      currentMapStyle.current = theme; // Track current theme
      
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: initialStyle,
        center: initialCenterRef.current,
        zoom: initialZoomRef.current,
        minZoom: 2,
        maxZoom: 18
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');

      // Add scale control
      map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-left');

      // Apply custom style adjustments after map loads
      map.current.on('style.load', () => {
        const mapInstance = map.current;
        
        try {
          // Reduce opacity of road labels for cleaner look
          const labelLayers = [
            'road-label',
            'road-number-shield',
            'road-exit-shield',
            'waterway-label',
            'natural-line-label',
            'natural-point-label',
            'poi-label',
            'transit-label'
          ];
          
          labelLayers.forEach(layerId => {
            safeSetPaintProperty(mapInstance, layerId, 'text-opacity', 0.65);
          });
          
          // Mute building colors
          safeSetPaintProperty(mapInstance, 'building', 'fill-opacity', 0.4);
          
          // Apply theme-aware water color
          const waterColor = getCSSVariable('--map-water', '#E3E9ED');
          safeSetPaintProperty(mapInstance, 'water', 'fill-color', waterColor);
        } catch (error) {
          // Style adjustments might fail on custom base maps - log and continue
          logWarning('Map style customization partially failed', {
            category: ErrorCategory.RENDERING,
            component: 'MapCanvas',
            action: 'style.load',
            metadata: {
              errorMessage: error.message,
              mapStyle: mapInstance.getStyle()?.name || 'unknown'
            }
          });
        }
      });

    } catch (error) {
      onErrorRef.current?.(error); // Always uses latest handler
    }

    // Cleanup
    return () => {
      if (popupRoot.current) {
        popupRoot.current.unmount();
        popupRoot.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle theme changes dynamically
  useEffect(() => {
    if (!map.current) return;
    
    // Check if the theme actually changed
    if (currentMapStyle.current !== theme) {
      currentMapStyle.current = theme;
      const newStyle = getMapboxStyle(theme);
      
      // Store current viewport to restore after style change
      const center = map.current.getCenter();
      const zoom = map.current.getZoom();
      
      // Update the Mapbox style
      map.current.setStyle(newStyle);
      
      // Re-apply our customizations and restore viewport after style loads
      map.current.once('style.load', () => {
        // Restore viewport position
        map.current.jumpTo({ center, zoom });
        
        // Update layer colors
        updateLayerColors(map.current);
      });
    } else {
      // If style didn't change, just update the layer colors
      updateLayerColors(map.current);
    }
  }, [theme]);

  // Recenter map when userLocation changes
  useEffect(() => {
    if (!map.current || !userLocation) return;
    
    // Only animate on first location acquisition
    const isFirstLocation = !initialCenterRef.current || 
      (initialCenterRef.current[0] === 139.6503 && initialCenterRef.current[1] === 35.6762);
    
    if (isFirstLocation) {
      map.current.flyTo({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: 14,
        duration: ANIMATION_DURATIONS.userLocationFlyTo,
        curve: 1.42,
        speed: 1.2,
        easing: (t) => {
          // Ease out cubic for smooth deceleration
          return 1 - Math.pow(1 - t, 3);
        }
      });
      
      // Update initial center to prevent re-animation
      initialCenterRef.current = [userLocation.longitude, userLocation.latitude];
    }
  }, [userLocation]);

  // Set up layers and event listeners
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance) return;

    // Store cleanup function to ensure it's always called
    let setupCleanup = () => {};

    // Wait for map to load
    const setupMap = () => {
      // Check if source exists, update or add it
      if (mapInstance.getSource('records')) {
        mapInstance.getSource('records').setData(geoJsonData);
        return () => {}; // Return stable no-op cleanup
      } else {
        mapInstance.addSource('records', {
          type: 'geojson',
          data: geoJsonData,
          cluster: true,
          clusterMaxZoom: 14,  // Show individual pins beyond zoom level 14
          clusterRadius: 60,    // Pixel radius for clustering (60px = comfortable touch target)
          clusterProperties: {
            // Future: Add category counts, etc.
          }
        });

        // Add layers only if they don't exist
        if (!mapInstance.getLayer('clusters')) {
          createClusterLayers(mapInstance);
          createPinLayers(mapInstance);
        }

        // Initialize popup
        initializePopup(mapInstance);

        // Store event handlers for cleanup
        const handleClusterLayerClick = (e) => {
          e.preventDefault();
          if (onClusterClick) {
            onClusterClick(e);
          }
        };
        
        const handlePinClick = (e) => {
          e.preventDefault();
          
          // Always close the hover popup on a confirmed tap
          if (popup.current) {
            popup.current.remove();
            cleanupPopupRoot();
          }
          
          const feature = e.features?.[0];
          if (feature && onPinClick) {
            onPinClick(feature.properties.id);
          }
        };

        const handlePinMouseEnter = (e) => {
          mapInstance.getCanvas().style.cursor = 'pointer';
          if (e.features[0] && popup.current && popupRoot.current) {
            const feature = e.features[0];
            const recordId = feature.properties.id;
            
            // Find the full record data
            const record = recordsRef.current?.find(r => r.id === recordId);
            
            if (record) {
              // Render into the persistent root
              popupRoot.current.render(<RecordHoverCard record={record} />);
              
              popup.current
                .setLngLat(feature.geometry.coordinates)
                .setDOMContent(popupContainer.current)
                .addTo(mapInstance);
            }
          }
        };

        const handlePinMouseLeave = () => {
          mapInstance.getCanvas().style.cursor = '';
          if (popup.current) {
            popup.current.remove();
          }
          cleanupPopupRoot();
        };

        const setCursorPointer = (e) => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        };

        const setCursorDefault = () => {
          mapInstance.getCanvas().style.cursor = '';
        };

        const handleMoveEnd = () => {
          const now = Date.now();
          if (now - lastViewportUpdate.current > ANIMATION_DURATIONS.debounceDelay) {
            lastViewportUpdate.current = now;
            const bounds = mapInstance.getBounds();
            if (onViewportChange) {
              onViewportChange({
                north: bounds.getNorth(),
                south: bounds.getSouth(),
                east: bounds.getEast(),
                west: bounds.getWest(),
                center: mapInstance.getCenter(),
                zoom: mapInstance.getZoom()
              });
            }
          }
          // Pin markers are now handled by WebGL layers - no manual updates needed
        };

        const handleError = (e) => {
          if (onError) {
            onError(e);
          }
        };

        // Attach event listeners
        mapInstance.on('click', 'clusters', handleClusterLayerClick);
        mapInstance.on('click', 'cluster-count', handleClusterLayerClick);
        
        // Attach click handler to both pin layers for consistent tap behavior
        ['pins', 'pins-hitarea'].forEach(layer =>
          mapInstance.on('click', layer, handlePinClick)
        );
        
        // Only attach hover handlers on devices with fine pointers (not touch devices)
        const isCoarsePointer = !window.matchMedia("(pointer: fine)").matches;
        
        mapInstance.on('mouseenter', 'clusters', setCursorPointer);
        mapInstance.on('mouseleave', 'clusters', setCursorDefault);
        mapInstance.on('mouseenter', 'cluster-count', setCursorPointer);
        mapInstance.on('mouseleave', 'cluster-count', setCursorDefault);
        
        if (!isCoarsePointer) {
          // Only enable hover cards on non-touch devices
          mapInstance.on('mouseenter', 'pins', handlePinMouseEnter);
          mapInstance.on('mouseleave', 'pins', handlePinMouseLeave);
        }

        mapInstance.on('moveend', handleMoveEnd);
        mapInstance.on('error', handleError);
        
        // Ensure parent gets initial viewport notification using idle event
        // This fixes the timing issue where first moveend fires before listener is attached
        mapInstance.once('idle', () => {
          handleMoveEnd();
        });

        // Notify parent that map is loaded
        if (onMapLoad) {
          onMapLoad();
        }

        // Cleanup function - store it in outer variable
        setupCleanup = () => {
          // Remove all event listeners
          mapInstance.off('click', 'clusters', handleClusterLayerClick);
          mapInstance.off('click', 'cluster-count', handleClusterLayerClick);
          
          // Remove click handlers from both pin layers
          ['pins', 'pins-hitarea'].forEach(layer =>
            mapInstance.off('click', layer, handlePinClick)
          );
          
          mapInstance.off('mouseenter', 'clusters', setCursorPointer);
          mapInstance.off('mouseleave', 'clusters', setCursorDefault);
          mapInstance.off('mouseenter', 'cluster-count', setCursorPointer);
          mapInstance.off('mouseleave', 'cluster-count', setCursorDefault);
          
          if (!isCoarsePointer) {
            // Only remove hover handlers if they were attached
            mapInstance.off('mouseenter', 'pins', handlePinMouseEnter);
            mapInstance.off('mouseleave', 'pins', handlePinMouseLeave);
          }

          mapInstance.off('moveend', handleMoveEnd);
          mapInstance.off('error', handleError);
          // Note: idle event uses once() so no cleanup needed
          
          // Clean up popup
          if (popup.current) {
            popup.current.remove();
            popup.current = null;
          }
          cleanupPopupRoot();
        };
        
        return setupCleanup;
      }
    };

    // Set up map when it's loaded
    if (mapInstance.loaded()) {
      setupCleanup = setupMap();
    } else {
      // Use once() to prevent duplicate listeners
      mapInstance.once('load', () => {
        setupCleanup = setupMap();
      });
    }

    // Return cleanup that always calls the real cleanup
    return () => {
      mapInstance.off('load', setupMap); // Remove listener if unmounting early
      setupCleanup(); // Always call the real cleanup
    };
  }, [geoJsonData, onClusterClick, onPinClick, onViewportChange, onMapLoad, onError, theme]); // All dependencies listed

  // Handle selected pin highlighting
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;
    
    // Check if the layer exists before updating
    if (map.current.getLayer('selected-pin')) {
      // Update selected pin layer filter
      const selectedId = selectedPinId || -1;
      map.current.setFilter('selected-pin', ['==', ['get', 'id'], selectedId]);
    }
  }, [selectedPinId]);

  return <div ref={mapContainer} className={styles.mapContainer} />;
});

MapCanvas.displayName = 'MapCanvas';

export default MapCanvas;